import React, { useState, useEffect } from 'react';
import { getExperianReportSoftPullData } from '../../AdminActionCreator.component';
import Loader from '../../../../presentationals/Loader/Loader.component';
import {
    Check,
    Copy,
    RefreshCw,
    X,
    ShieldCheck,
    CreditCard,
    AlertTriangle,
    Calendar,
    User,
    FileText,
    TrendingUp,
    Search,
    Activity,
    Layers,
    ChevronDown,
    ChevronUp,
    Phone,
    Mail,
    MapPin,
    DollarSign
} from 'lucide-react';
import NoData from '../../../../../images/nodata.png';

// Helper to format YYYYMMDD dates
const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const s = String(dateStr).trim();
    if (s.length === 8 && !isNaN(s)) {
        return `${s.substring(6, 8)}/${s.substring(4, 6)}/${s.substring(0, 4)}`;
    }
    return s;
};

// Helper to format currency
const formatCurrency = (val) => {
    if (val === null || val === undefined || val === '') return '-';
    const num = Number(val);
    if (isNaN(num)) return val;
    return '₹' + num.toLocaleString('en-IN');
};

// Experian Account Type Codes mapping
const getAccountTypeLabel = (code) => {
    if (!code) return 'Credit Account';
    const map = {
        '01': 'Auto Loan (Personal)',
        '02': 'Housing Loan',
        '03': 'Property Loan',
        '04': 'Loan against Securities',
        '05': 'Personal Loan',
        '06': 'Consumer / Personal Loan',
        '07': 'Gold Loan',
        '08': 'Education Loan',
        '09': 'Loan Against Deposit',
        '10': 'Credit Card',
        '11': 'Two-Wheeler Loan',
        '12': 'Commercial Vehicle Loan',
        '17': 'Credit Card',
        '31': 'Overdraft',
        '32': 'Two-Wheeler Loan',
        '99': 'Unspecified / Other'
    };
    return map[String(code).padStart(2, '0')] || `Account Type (${code})`;
};

// DPD status badge styling
const getDpdBadge = (dpd) => {
    if (dpd === '0' || dpd === '000' || dpd === 'STD') {
        return { bg: '#dcfce7', color: '#15803d', label: 'STD (0)' };
    }
    if (dpd === '?' || dpd === 'XXX') {
        return { bg: '#f1f5f9', color: '#64748b', label: dpd };
    }
    const num = parseInt(dpd, 10);
    if (!isNaN(num)) {
        if (num === 0) return { bg: '#dcfce7', color: '#15803d', label: '0 DPD' };
        if (num <= 30) return { bg: '#fef3c7', color: '#b45309', label: `${num} DPD` };
        return { bg: '#fee2e2', color: '#b91c1c', label: `${num} DPD` };
    }
    return { bg: '#e2e8f0', color: '#475569', label: dpd || '-' };
};

export default function Experian({ admin, userdata, hnadleEquifaxOpenPopup }) {
    const [loader, setLoader] = useState(false);
    const [data, setData] = useState({});
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'accounts' | 'enquiries' | 'profile'
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedAccount, setExpandedAccount] = useState(null);

    useEffect(() => {
        APi(false);
    }, []);

    const APi = (val) => {
        setLoader(true);
        getExperianReportSoftPullData(userdata?.loanId, val, callback => {
            setLoader(false);
            setData(callback);
        });
    };

    // Data parsing
    const resData = data?.experion_data?.data || data?.experion_data || data?.data || data;
    const creditReport = resData?.credit_report || resData;
    const score = data?.cibil_score ?? resData?.credit_score ?? creditReport?.SCORE?.BureauScore ?? creditReport?.SCORE?.FCIREXScore;

    const caisSummary = creditReport?.CAIS_Account?.CAIS_Summary;
    const totalBalance = caisSummary?.Total_Outstanding_Balance;
    const creditAccountsSummary = caisSummary?.Credit_Account;
    const caisAccountsRaw = creditReport?.CAIS_Account?.CAIS_Account_DETAILS;
    const accountsList = Array.isArray(caisAccountsRaw) ? caisAccountsRaw : (caisAccountsRaw ? [caisAccountsRaw] : []);

    const capsSummary = creditReport?.TotalCAPS_Summary || creditReport?.NonCreditCAPS?.NonCreditCAPS_Summary;
    const capsDetailsRaw = creditReport?.NonCreditCAPS?.CAPS_Application_Details;
    const enquiriesList = Array.isArray(capsDetailsRaw) ? capsDetailsRaw : (capsDetailsRaw ? [capsDetailsRaw] : []);

    const profileHeader = creditReport?.CreditProfileHeader;
    const headerInfo = creditReport?.Header;

    const hasJsonReport = score !== undefined || accountsList.length > 0 || enquiriesList.length > 0 || caisSummary;

    // Filter accounts by search
    const filteredAccounts = accountsList.filter(acc => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        const subName = (acc.Subscriber_Name || '').toLowerCase();
        const accNo = (acc.Account_Number || '').toLowerCase();
        const typeLabel = getAccountTypeLabel(acc.Account_Type).toLowerCase();
        return subName.includes(term) || accNo.includes(term) || typeLabel.includes(term);
    });

    const getScoreTheme = (scoreNum) => {
        if (!scoreNum || scoreNum <= 0) return { color: '#64748b', bg: '#f1f5f9', label: 'N/A' };
        if (scoreNum >= 750) return { color: '#16a34a', bg: '#dcfce7', label: 'Excellent' };
        if (scoreNum >= 700) return { color: '#2563eb', bg: '#dbeafe', label: 'Good' };
        if (scoreNum >= 650) return { color: '#d97706', bg: '#fef3c7', label: 'Fair' };
        return { color: '#dc2626', bg: '#fee2e2', label: 'Poor' };
    };

    const scoreTheme = getScoreTheme(score);

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '20px'
        }}>
            <div style={{
                background: '#ffffff',
                borderRadius: '16px',
                width: '1100px',
                maxWidth: '96vw',
                maxHeight: '92vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                overflow: 'hidden',
                position: 'relative'
            }}>
                {loader && <Loader />}

                {/* MODAL HEADER */}
                <div style={{
                    padding: '18px 24px',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: '#f8fafc',
                    flexShrink: 0
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: '#eff6ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#2563eb'
                        }}>
                            <ShieldCheck size={22} style={{ width: "auto", margin: "0" }} />
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>
                                Experian Credit Bureau Report
                            </h3>
                            <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                <span>User ID: <b>{data?.user_id || userdata?.userId || '-'}</b></span>
                                {profileHeader?.ReportDate && <span>Report Date: <b>{formatDate(profileHeader.ReportDate)}</b></span>}
                                {profileHeader?.ReportNumber && <span>Report #: <b>{profileHeader.ReportNumber}</b></span>}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <button
                            onClick={() => APi(true)}
                            disabled={loader}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: '#16a34a',
                                color: '#ffffff',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontWeight: '700',
                                cursor: loader ? 'not-allowed' : 'pointer',
                                transition: 'all 0.15s ease',
                                boxShadow: '0 2px 4px rgba(22, 163, 74, 0.2)'
                            }}
                        >
                            <RefreshCw size={14} style={{ width: "auto", margin: "0" }} />
                            Regenerate Report
                        </button>
                        <button
                            onClick={() => hnadleEquifaxOpenPopup(false)}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#64748b',
                                cursor: 'pointer',
                                padding: '6px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <X size={22} style={{ width: "auto", margin: "0" }} />
                        </button>
                    </div>
                </div>

                {/* CONTENT AREA */}
                {hasJsonReport ? (
                    <>
                        {/* NAVIGATION TABS */}
                        <div style={{
                            display: 'flex',
                            borderBottom: '1px solid #e2e8f0',
                            padding: '0 24px',
                            background: '#ffffff',
                            gap: '4px',
                            flexShrink: 0
                        }}>
                            {[
                                { id: 'overview', label: 'Overview & Score', icon: Layers },
                                { id: 'accounts', label: `Credit Accounts (${accountsList.length})`, icon: CreditCard },
                                { id: 'enquiries', label: `CAPS / Enquiries (${enquiriesList.length})`, icon: FileText },
                                { id: 'profile', label: 'Identity & Details', icon: User }
                            ].map(tab => {
                                const TabIcon = tab.icon;
                                const isActive = activeTab === tab.id;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '12px 18px',
                                            border: 'none',
                                            borderBottom: isActive ? '3px solid #2563eb' : '3px solid transparent',
                                            background: 'transparent',
                                            color: isActive ? '#2563eb' : '#64748b',
                                            fontWeight: isActive ? '800' : '600',
                                            fontSize: '13px',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease'
                                        }}
                                    >
                                        <TabIcon size={15} style={{ width: "auto", margin: "0" }} />
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>

                        <div style={{ overflowY: 'auto', padding: '24px', flex: 1, background: '#f8fafc' }}>

                            {/* TAB 1: OVERVIEW */}
                            {activeTab === 'overview' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    
                                    {/* HERO SCORE & SUMMARY ROW */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '260px 1fr',
                                        gap: '20px'
                                    }}>
                                        {/* SCORE CARD */}
                                        <div style={{
                                            background: '#ffffff',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '14px',
                                            padding: '24px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            textAlign: 'center',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                                        }}>
                                            <span style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                Experian Bureau Score
                                            </span>
                                            <div style={{
                                                fontSize: '52px',
                                                fontWeight: '900',
                                                color: scoreTheme.color,
                                                lineHeight: 1,
                                                margin: '14px 0 10px 0'
                                            }}>
                                                {score ?? 'N/A'}
                                            </div>
                                            <span style={{
                                                fontSize: '12px',
                                                fontWeight: '800',
                                                color: scoreTheme.color,
                                                background: scoreTheme.bg,
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}>
                                                {scoreTheme.label}
                                            </span>
                                            {creditReport?.SCORE?.FCIREXScore && (
                                                <div style={{ marginTop: '14px', fontSize: '11px', color: '#94a3b8' }}>
                                                    FCIREX Score: <b>{creditReport.SCORE.FCIREXScore}</b>
                                                </div>
                                            )}
                                        </div>

                                        {/* CREDIT ACCOUNT STATS */}
                                        <div style={{
                                            background: '#ffffff',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '14px',
                                            padding: '20px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                                                    Credit Accounts Overview
                                                </h4>
                                                <span style={{ fontSize: '12px', color: '#64748b' }}>
                                                    Total: <b>{creditAccountsSummary?.CreditAccountTotal ?? accountsList.length}</b>
                                                </span>
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                                                <div style={{ background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Active</span>
                                                    <div style={{ fontSize: '20px', fontWeight: '900', color: '#2563eb', marginTop: '4px' }}>
                                                        {creditAccountsSummary?.CreditAccountActive ?? '-'}
                                                    </div>
                                                </div>
                                                <div style={{ background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Closed</span>
                                                    <div style={{ fontSize: '20px', fontWeight: '900', color: '#475569', marginTop: '4px' }}>
                                                        {creditAccountsSummary?.CreditAccountClosed ?? '-'}
                                                    </div>
                                                </div>
                                                <div style={{ background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Default</span>
                                                    <div style={{ fontSize: '20px', fontWeight: '900', color: '#dc2626', marginTop: '4px' }}>
                                                        {creditAccountsSummary?.CreditAccountDefault ?? '0'}
                                                    </div>
                                                </div>
                                                <div style={{ background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Suit Filed</span>
                                                    <div style={{ fontSize: '20px', fontWeight: '900', color: '#d97706', marginTop: '4px' }}>
                                                        {creditAccountsSummary?.CADSuitFiledCurrentBalance ?? '0'}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* OUTSTANDING BALANCE BREAKDOWN */}
                                            {totalBalance && (
                                                <div style={{
                                                    marginTop: '16px',
                                                    paddingTop: '14px',
                                                    borderTop: '1px solid #f1f5f9',
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                                    gap: '12px'
                                                }}>
                                                    <div>
                                                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Total Outstanding</span>
                                                        <div style={{ fontSize: '15px', fontWeight: '800', color: '#0f172a', marginTop: '2px' }}>
                                                            {formatCurrency(totalBalance.Outstanding_Balance_All)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: '600' }}>
                                                            Secured ({totalBalance.Outstanding_Balance_Secured_Percentage ?? '0'}%)
                                                        </span>
                                                        <div style={{ fontSize: '15px', fontWeight: '800', color: '#15803d', marginTop: '2px' }}>
                                                            {formatCurrency(totalBalance.Outstanding_Balance_Secured)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <span style={{ fontSize: '11px', color: '#d97706', fontWeight: '600' }}>
                                                            Unsecured ({totalBalance.Outstanding_Balance_UnSecured_Percentage ?? '0'}%)
                                                        </span>
                                                        <div style={{ fontSize: '15px', fontWeight: '800', color: '#b45309', marginTop: '2px' }}>
                                                            {formatCurrency(totalBalance.Outstanding_Balance_UnSecured)}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* CAPS ENQUIRIES SUMMARY */}
                                    {capsSummary && (
                                        <div style={{
                                            background: '#ffffff',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '14px',
                                            padding: '20px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                                        }}>
                                            <h4 style={{ margin: '0 0 14px 0', fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                                                Total Enquiries (CAPS Summary)
                                            </h4>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                                                <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '10px', textAlign: 'center', border: '1px solid #f1f5f9' }}>
                                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Last 7 Days</span>
                                                    <div style={{ fontSize: '20px', fontWeight: '900', color: '#0f172a', marginTop: '4px' }}>
                                                        {capsSummary.TotalCAPSLast7Days || capsSummary.NonCreditCAPSLast7Days || '0'}
                                                    </div>
                                                </div>
                                                <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '10px', textAlign: 'center', border: '1px solid #f1f5f9' }}>
                                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Last 30 Days</span>
                                                    <div style={{ fontSize: '20px', fontWeight: '900', color: '#0f172a', marginTop: '4px' }}>
                                                        {capsSummary.TotalCAPSLast30Days || capsSummary.NonCreditCAPSLast30Days || '0'}
                                                    </div>
                                                </div>
                                                <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '10px', textAlign: 'center', border: '1px solid #f1f5f9' }}>
                                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Last 90 Days</span>
                                                    <div style={{ fontSize: '20px', fontWeight: '900', color: '#0f172a', marginTop: '4px' }}>
                                                        {capsSummary.TotalCAPSLast90Days || capsSummary.NonCreditCAPSLast90Days || '0'}
                                                    </div>
                                                </div>
                                                <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '10px', textAlign: 'center', border: '1px solid #f1f5f9' }}>
                                                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Last 180 Days</span>
                                                    <div style={{ fontSize: '20px', fontWeight: '900', color: '#0f172a', marginTop: '4px' }}>
                                                        {capsSummary.TotalCAPSLast180Days || capsSummary.NonCreditCAPSLast180Days || '0'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* RECENT ACCOUNTS PREVIEW */}
                                    {accountsList.length > 0 && (
                                        <div style={{
                                            background: '#ffffff',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '14px',
                                            padding: '20px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                                <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                                                    Recent Credit Accounts
                                                </h4>
                                                <button
                                                    onClick={() => setActiveTab('accounts')}
                                                    style={{
                                                        background: 'transparent',
                                                        border: 'none',
                                                        color: '#2563eb',
                                                        fontWeight: '700',
                                                        fontSize: '12px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    View All ({accountsList.length}) →
                                                </button>
                                            </div>

                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                {accountsList.slice(0, 5).map((acc, index) => (
                                                    <div key={index} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        padding: '12px 16px',
                                                        background: '#f8fafc',
                                                        border: '1px solid #f1f5f9',
                                                        borderRadius: '10px'
                                                    }}>
                                                        <div>
                                                            <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a' }}>
                                                                {acc.Subscriber_Name || 'Lender / Institution'}
                                                            </div>
                                                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                                                {getAccountTypeLabel(acc.Account_Type)} • Acc #: <b>{acc.Account_Number || '-'}</b>
                                                            </div>
                                                        </div>
                                                        <div style={{ textAlign: 'right' }}>
                                                            <div style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                                                                {formatCurrency(acc.Current_Balance)}
                                                            </div>
                                                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                                                Opened: <b>{formatDate(acc.Open_Date)}</b>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                </div>
                            )}

                            {/* TAB 2: CREDIT ACCOUNTS LIST */}
                            {activeTab === 'accounts' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    
                                    {/* SEARCH & FILTERS */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        background: '#ffffff',
                                        padding: '12px 18px',
                                        borderRadius: '12px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, maxWidth: '400px' }}>
                                            <Search size={16} color="#64748b" style={{ width: "auto", margin: "0" }} />
                                            <input
                                                type="text"
                                                placeholder="Search by Subscriber Name, Acc No, Loan Type..."
                                                value={searchTerm}
                                                onChange={e => setSearchTerm(e.target.value)}
                                                style={{
                                                    border: 'none',
                                                    outline: 'none',
                                                    fontSize: '13px',
                                                    width: '100%',
                                                    background: 'transparent'
                                                }}
                                            />
                                        </div>
                                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
                                            Showing {filteredAccounts.length} of {accountsList.length} accounts
                                        </span>
                                    </div>

                                    {/* ACCOUNTS ACCORDION / LIST */}
                                    {filteredAccounts.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '40px', background: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                                            <NoData style={{ width: '120px', opacity: 0.6 }} />
                                            <p style={{ marginTop: '12px', color: '#64748b', fontSize: '13px' }}>No credit accounts match your search.</p>
                                        </div>
                                    ) : (
                                        filteredAccounts.map((acc, index) => {
                                            const isExpanded = expandedAccount === index;
                                            const history = Array.isArray(acc.CAIS_Account_History) ? acc.CAIS_Account_History : [];
                                            const reviews = Array.isArray(acc.Account_Review_Data) ? acc.Account_Review_Data : [];

                                            return (
                                                <div key={index} style={{
                                                    background: '#ffffff',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '14px',
                                                    overflow: 'hidden',
                                                    boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
                                                    transition: 'all 0.15s ease'
                                                }}>
                                                    {/* ACCORDION HEADER */}
                                                    <div
                                                        onClick={() => setExpandedAccount(isExpanded ? null : index)}
                                                        style={{
                                                            padding: '16px 20px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            cursor: 'pointer',
                                                            background: isExpanded ? '#f8fafc' : '#ffffff',
                                                            borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                                            <div style={{
                                                                width: '36px',
                                                                height: '36px',
                                                                borderRadius: '10px',
                                                                background: '#eff6ff',
                                                                color: '#2563eb',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}>
                                                                <CreditCard size={18} style={{ width: "auto", margin: "0" }} />
                                                            </div>
                                                            <div>
                                                                <div style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                                                                    {acc.Subscriber_Name || 'Institution'}
                                                                </div>
                                                                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                                                    {getAccountTypeLabel(acc.Account_Type)} • Acc #: <b>{acc.Account_Number || '-'}</b>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                                                            <div style={{ textAlign: 'right' }}>
                                                                <div style={{ fontSize: '11px', color: '#64748b' }}>Current Balance</div>
                                                                <div style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                                                                    {formatCurrency(acc.Current_Balance)}
                                                                </div>
                                                            </div>
                                                            <div style={{ textAlign: 'right' }}>
                                                                <div style={{ fontSize: '11px', color: '#64748b' }}>High Credit / Limit</div>
                                                                <div style={{ fontSize: '14px', fontWeight: '700', color: '#475569' }}>
                                                                    {formatCurrency(acc.Highest_Credit_or_Original_Loan_Amount || acc.Credit_Limit_Amount)}
                                                                </div>
                                                            </div>
                                                            <div style={{ textAlign: 'right' }}>
                                                                <div style={{ fontSize: '11px', color: '#64748b' }}>Overdue</div>
                                                                <div style={{ fontSize: '14px', fontWeight: '800', color: Number(acc.Amount_Past_Due) > 0 ? '#dc2626' : '#16a34a' }}>
                                                                    {formatCurrency(acc.Amount_Past_Due)}
                                                                </div>
                                                            </div>
                                                            {isExpanded ? <ChevronUp size={18} color="#64748b" style={{ width: "auto", margin: "0" }} /> : <ChevronDown size={18} color="#64748b" style={{ width: "auto", margin: "0" }} />}
                                                        </div>
                                                    </div>

                                                    {/* ACCORDION DETAILS */}
                                                    {isExpanded && (
                                                        <div style={{ padding: '20px', background: '#ffffff', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                                            
                                                            {/* ACCOUNT METRICS GRID */}
                                                            <div style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: 'repeat(4, 1fr)',
                                                                gap: '12px',
                                                                background: '#f8fafc',
                                                                padding: '14px',
                                                                borderRadius: '10px',
                                                                border: '1px solid #f1f5f9'
                                                            }}>
                                                                <div>
                                                                    <span style={{ fontSize: '11px', color: '#64748b' }}>Open Date</span>
                                                                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', marginTop: '2px' }}>
                                                                        {formatDate(acc.Open_Date)}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <span style={{ fontSize: '11px', color: '#64748b' }}>Date Reported</span>
                                                                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', marginTop: '2px' }}>
                                                                        {formatDate(acc.Date_Reported)}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <span style={{ fontSize: '11px', color: '#64748b' }}>Last Payment Date</span>
                                                                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', marginTop: '2px' }}>
                                                                        {formatDate(acc.Date_of_Last_Payment)}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <span style={{ fontSize: '11px', color: '#64748b' }}>Tenure / Frequency</span>
                                                                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', marginTop: '2px' }}>
                                                                        {acc.Repayment_Tenure ? `${acc.Repayment_Tenure} M` : '-'} {acc.Terms_Frequency ? `(${acc.Terms_Frequency})` : ''}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* DPD / ACCOUNT HISTORY TREND */}
                                                            {history.length > 0 && (
                                                                <div>
                                                                    <h5 style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                                                        Monthly DPD Payment History
                                                                    </h5>
                                                                    <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '6px' }}>
                                                                        {history.map((h, hIdx) => {
                                                                            const badge = getDpdBadge(h.Days_Past_Due);
                                                                            return (
                                                                                <div key={hIdx} style={{
                                                                                    background: badge.bg,
                                                                                    border: `1px solid ${badge.color}33`,
                                                                                    borderRadius: '8px',
                                                                                    padding: '8px 12px',
                                                                                    textAlign: 'center',
                                                                                    minWidth: '70px',
                                                                                    flexShrink: 0
                                                                                }}>
                                                                                    <div style={{ fontSize: '10px', color: '#64748b', fontWeight: '700' }}>
                                                                                        {h.Month}/{h.Year}
                                                                                    </div>
                                                                                    <div style={{ fontSize: '12px', fontWeight: '900', color: badge.color, marginTop: '2px' }}>
                                                                                        {badge.label}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* ACCOUNT REVIEWS TABLE */}
                                                            {reviews.length > 0 && (
                                                                <div>
                                                                    <h5 style={{ margin: '0 0 10px 0', fontSize: '12px', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                                                        Account Review Snapshots
                                                                    </h5>
                                                                    <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                                                            <thead>
                                                                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                                                                                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Period</th>
                                                                                    <th style={{ padding: '8px 12px', textAlign: 'right' }}>Current Balance</th>
                                                                                    <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actual Payment</th>
                                                                                    <th style={{ padding: '8px 12px', textAlign: 'right' }}>EMI Amount</th>
                                                                                    <th style={{ padding: '8px 12px', textAlign: 'right' }}>Amount Past Due</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {reviews.map((rev, rIdx) => (
                                                                                    <tr key={rIdx} style={{ borderBottom: rIdx === reviews.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                                                                                        <td style={{ padding: '8px 12px', fontWeight: '700' }}>{rev.Month}/{rev.Year}</td>
                                                                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '700' }}>{formatCurrency(rev.Current_Balance)}</td>
                                                                                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatCurrency(rev.Actual_Payment_Amount)}</td>
                                                                                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatCurrency(rev.EMI_Amount)}</td>
                                                                                        <td style={{ padding: '8px 12px', textAlign: 'right', color: Number(rev.Amount_Past_Due) > 0 ? '#dc2626' : '#16a34a', fontWeight: '700' }}>{formatCurrency(rev.Amount_Past_Due)}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            )}

                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}

                                </div>
                            )}

                            {/* TAB 3: CAPS / ENQUIRIES */}
                            {activeTab === 'enquiries' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    
                                    <div style={{
                                        background: '#ffffff',
                                        borderRadius: '14px',
                                        border: '1px solid #e2e8f0',
                                        padding: '20px',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                                    }}>
                                        <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                                            Enquiry & Search History (CAPS)
                                        </h4>

                                        {enquiriesList.length === 0 ? (
                                            <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>No enquiry details recorded.</p>
                                        ) : (
                                            <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                                    <thead>
                                                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                                                            <th style={{ padding: '10px 14px' }}>Request Date</th>
                                                            <th style={{ padding: '10px 14px' }}>Subscriber / Lender</th>
                                                            <th style={{ padding: '10px 14px' }}>Finance Purpose</th>
                                                            <th style={{ padding: '10px 14px', textAlign: 'right' }}>Amount Financed</th>
                                                            <th style={{ padding: '10px 14px' }}>Report Number</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {enquiriesList.map((enq, idx) => (
                                                            <tr key={idx} style={{ borderBottom: idx === enquiriesList.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                                                                <td style={{ padding: '12px 14px', fontWeight: '700', color: '#0f172a' }}>
                                                                    {formatDate(enq.Date_of_Request)}
                                                                </td>
                                                                <td style={{ padding: '12px 14px', fontWeight: '700', color: '#2563eb' }}>
                                                                    {enq.Subscriber_Name || enq.Subscriber_code || 'Institution'}
                                                                </td>
                                                                <td style={{ padding: '12px 14px', color: '#475569' }}>
                                                                    Purpose Code: <b>{enq.Finance_Purpose || enq.Enquiry_Reason || '-'}</b>
                                                                </td>
                                                                <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: '800', color: '#0f172a' }}>
                                                                    {formatCurrency(enq.Amount_Financed)}
                                                                </td>
                                                                <td style={{ padding: '12px 14px', color: '#64748b', fontFamily: 'monospace' }}>
                                                                    {enq.ReportNumber || '-'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>

                                </div>
                            )}

                            {/* TAB 4: PROFILE & IDENTITY */}
                            {activeTab === 'profile' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    
                                    {/* HEADER METADATA CARD */}
                                    <div style={{
                                        background: '#ffffff',
                                        borderRadius: '14px',
                                        border: '1px solid #e2e8f0',
                                        padding: '20px',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
                                    }}>
                                        <h4 style={{ margin: '0 0 14px 0', fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>
                                            Credit Profile Header Information
                                        </h4>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
                                            <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                                                <span style={{ fontSize: '11px', color: '#64748b' }}>Report Number</span>
                                                <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a', marginTop: '2px' }}>
                                                    {profileHeader?.ReportNumber || '-'}
                                                </div>
                                            </div>
                                            <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                                                <span style={{ fontSize: '11px', color: '#64748b' }}>Customer Ref ID</span>
                                                <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a', marginTop: '2px' }}>
                                                    {profileHeader?.CustomerReferenceID || '-'}
                                                </div>
                                            </div>
                                            <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                                                <span style={{ fontSize: '11px', color: '#64748b' }}>Version</span>
                                                <div style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a', marginTop: '2px' }}>
                                                    {profileHeader?.Version || '-'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ADDRESSES & PHONES FROM ACCOUNTS */}
                                    {accountsList.length > 0 && (() => {
                                        const addresses = [];
                                        const phones = [];
                                        accountsList.forEach(acc => {
                                            if (Array.isArray(acc.CAIS_Holder_Address_Details)) {
                                                acc.CAIS_Holder_Address_Details.forEach(addr => {
                                                    const fullStr = [
                                                        addr.First_Line_Of_Address_non_normalized,
                                                        addr.Second_Line_Of_Address_non_normalized,
                                                        addr.Third_Line_Of_Address_non_normalized,
                                                        addr.ZIP_Postal_Code_non_normalized
                                                    ].filter(Boolean).join(', ');
                                                    if (fullStr && !addresses.includes(fullStr)) addresses.push(fullStr);
                                                });
                                            }
                                            if (Array.isArray(acc.CAIS_Holder_Phone_Details)) {
                                                acc.CAIS_Holder_Phone_Details.forEach(ph => {
                                                    const pNum = ph.Mobile_Telephone_Number || ph.Telephone_Number;
                                                    if (pNum && !phones.includes(pNum)) phones.push(pNum);
                                                });
                                            }
                                        });

                                        return (
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                                <div style={{ background: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '20px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                                        <MapPin size={16} color="#2563eb" style={{ width: "auto", margin: "0" }} />
                                                        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>Reported Addresses</h4>
                                                    </div>
                                                    {addresses.length === 0 ? (
                                                        <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>No addresses recorded.</p>
                                                    ) : (
                                                        <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#334155', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                            {addresses.map((a, i) => <li key={i}>{a}</li>)}
                                                        </ul>
                                                    )}
                                                </div>

                                                <div style={{ background: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '20px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                                        <Phone size={16} color="#2563eb" style={{ width: "auto", margin: "0" }} />
                                                        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>Reported Phone Numbers</h4>
                                                    </div>
                                                    {phones.length === 0 ? (
                                                        <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>No phone numbers recorded.</p>
                                                    ) : (
                                                        <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#334155', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            {phones.map((p, i) => <li key={i}>{p}</li>)}
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                </div>
                            )}

                        </div>
                    </>
                ) : data?.url ? (
                    // IFRAME FALLBACK IF API RETURNS URL
                    <div style={{ padding: '20px', flex: 1 }}>
                        <iframe src={data.url} height="100%" width="100%" style={{ border: 'none', borderRadius: '8px', minHeight: '500px' }} title="Experian Report PDF" />
                    </div>
                ) : (
                    // NO DATA STATE
                    <div style={{ padding: '60px 20px', textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={NoData} style={{ width: '180px', opacity: 0.7 }} alt="No data available" />
                        <h4 style={{ margin: '20px 0 6px 0', fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
                            No Experian Report Available
                        </h4>
                        <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#64748b', maxWidth: '400px' }}>
                            Experian credit data has not been fetched yet or returned an empty response. Click regenerate to fetch soft pull report.
                        </p>
                        <button
                            onClick={() => APi(true)}
                            disabled={loader}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                background: '#2563eb',
                                color: '#ffffff',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '10px',
                                fontSize: '14px',
                                fontWeight: '700',
                                cursor: 'pointer'
                            }}
                        >
                            <RefreshCw size={16} style={{ width: "auto", margin: "0" }} />
                            Fetch Experian Report Now
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
